import { FaPaperPlane, FaMicrophone } from 'react-icons/fa';
import styled from 'styled-components';
import {
  EVEN_LIGHTER_GREY_COLOR,
  LIGHT_GREY_COLOR,
  PRIMARY_TEXT_COLOR,
  SECONDARY_TEXT_COLOR,
} from '../../constants';
import { desktopMediaQuery, getIsOnDesktop } from '../../styleHelpers';
/*
import ExtraOptionsIconAndMenu from './ExtraOptionsIconAndMenu';
<ExtraOptionsIconAndMenu handleFocusOnInput={handleFocusOnInput} />
*/
import { useRef, useEffect } from 'react';
import { useChatContext } from '../../context/ChatContextProvider';
import { isStandalone, waitSeconds } from '../../components/helpers';
import ExtraOptionsIconAndMenu from './ExtraOptionsIconAndMenu';
import useRecordAudio from '../../hooks/chat/useRecordAudio';
import useHandleAudio2Text from '../../hooks/chat/useHandleAudio2Text';
import { useLoggedInUserContext } from '../../context/LoggedInUserContextProvider';

const MAX_MESSAGE_LENGTH = 280;

interface ChatInputProps {
  chatInputValue: string;
  setChatInputValue: (message: string | ((prev: string) => string)) => void;
  handleSendMessage: () => void;
  placeholder: string;
  disableInput?: boolean;
}

const ChatInput = ({
  chatInputValue,
  setChatInputValue,
  handleSendMessage,
  placeholder,
}: ChatInputProps) => {
  const {
    isFocusedOnKeyboard,
    setIsFocusedOnKeyboard,
    botIsTyping,
    isDoingAction,
  } = useChatContext();
  const emphasizeInput = false; //placeholder === 'Write only your name...';

  const { loggedInUser } = useLoggedInUserContext();

  const isOnDesktop = getIsOnDesktop();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { isRecording, startRecording, stopRecording, recordingError } =
    useRecordAudio();
  const {
    isConverting,
    convertAudioToText,
    error: conversionError,
  } = useHandleAudio2Text();

  useEffect(() => {
    if (isOnDesktop && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOnDesktop]);

  useEffect(() => {
    if (recordingError) {
      console.error('Recording error:', recordingError);
      // You could add a toast notification here
    }
  }, [recordingError]);

  useEffect(() => {
    if (conversionError) {
      console.error('Conversion error:', conversionError);
      // You could add a toast notification here
    }
  }, [conversionError]);

  const adjustHeight = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [chatInputValue]);

  const onFocus = async () => {
    if (isOnDesktop) return;
    await waitSeconds(0.2);
    setIsFocusedOnKeyboard(true);
  };

  const onBlur = async () => {
    await waitSeconds(0.3);
    setIsFocusedOnKeyboard(false);
  };

  const onSendMessage = async () => {
    if (botIsTyping) return;

    handleSendMessage();
    await waitSeconds(0.2);

    if (isOnDesktop) {
      textareaRef.current?.focus();
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > MAX_MESSAGE_LENGTH) {
      e.target.value = e.target.value.slice(0, MAX_MESSAGE_LENGTH);
    }
    setChatInputValue(e.target.value);
  };

  const handleFocusOnInput = () => {
    textareaRef.current?.focus();
    setIsFocusedOnKeyboard(true);
  };

  const handleMicrophoneClick = async () => {
    console.log('Microphone clicked, current recording state:', isRecording);
    if (isRecording) {
      console.log('Stopping recording...');
      const audioBlob = await stopRecording();
      console.log(
        'Recording stopped, audio blob received:',
        !!audioBlob,
        audioBlob
          ? `Size: ${audioBlob.size} bytes, Type: ${audioBlob.type}`
          : 'No blob',
      );

      if (audioBlob) {
        console.log('Attempting to convert audio to text...');
        const transcription = await convertAudioToText(audioBlob);
        console.log('Transcription result:', transcription);

        if (transcription) {
          console.log('Setting chat input value with transcription');
          setChatInputValue((prev: string) => {
            const newValue = prev.trim()
              ? `${prev} ${transcription}`
              : transcription;
            return newValue.length > MAX_MESSAGE_LENGTH
              ? newValue.slice(0, MAX_MESSAGE_LENGTH)
              : newValue;
          });
        } else {
          console.error('Transcription failed or returned empty');
        }
      } else {
        console.error('No audio blob was returned from stopRecording');
      }
    } else {
      console.log('Starting recording...');
      try {
        await startRecording();
        console.log('Recording started successfully');
      } catch (error) {
        console.error('Error starting recording:', error);
      }
    }
  };

  const showMicrophoneButton = loggedInUser?.username === 'alpo';

  const isSendDisabled = botIsTyping || chatInputValue.trim() === '';
  const isInputDisabled = isRecording || isConverting;

  return (
    <InputContainer
      emphasizeInput={emphasizeInput}
      isFocusedOnKeyboard={isFocusedOnKeyboard}
    >
      <ExtraOptionsIconAndMenu handleFocusOnInput={handleFocusOnInput} />
      <IsDoingActionStar isDoingAction={isDoingAction} />
      <StyledTextarea
        ref={textareaRef}
        emphasizeInput={emphasizeInput}
        placeholder={isRecording ? 'Recording...' : placeholder}
        value={chatInputValue}
        onChange={handleOnChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage();
          }
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        maxLength={MAX_MESSAGE_LENGTH}
        autoComplete="on"
        rows={1}
        disabled={isInputDisabled}
      />
      <IsDoingActionStar isDoingAction={isDoingAction} />
      {showMicrophoneButton && (
        <MicrophoneButton
          onClick={handleMicrophoneClick}
          isRecording={isRecording}
          disabled={isConverting || botIsTyping}
        >
          <FaMicrophone />
        </MicrophoneButton>
      )}
      <ContainerForSendingButton>
        <PaperPlaneIcon onClick={onSendMessage} disabled={isSendDisabled} />
      </ContainerForSendingButton>
    </InputContainer>
  );
};

export default ChatInput;

const IsDoingActionStar = ({ isDoingAction }: { isDoingAction: boolean }) => {
  if (isDoingAction) {
    return (
      <div
        style={{
          color: SECONDARY_TEXT_COLOR,
          fontSize: '20px',
          marginRight: '8px',
          marginLeft: '8px',
        }}
      >
        *
      </div>
    );
  }
  return null;
};

const InputContainer = styled.div<{
  emphasizeInput: boolean;
  isFocusedOnKeyboard: boolean;
}>`
  position: sticky;
  bottom: 0;

  display: flex;
  align-items: center;
  width: 97%;

  border: 1.5px solid
    ${(props) =>
      props.emphasizeInput ? EVEN_LIGHTER_GREY_COLOR : LIGHT_GREY_COLOR};

  border-radius: 8px;
  margin-left: 6px;

  margin-bottom: ${() => (isStandalone() ? '0' : '16px')};

  ${desktopMediaQuery} {
    margin-bottom: 0;
  }
`;
const StyledTextarea = styled.textarea<{
  emphasizeInput: boolean;
  rows: number;
  disabled?: boolean;
}>`
  width: 100%;
  border: none;
  resize: none;
  overflow: hidden;
  padding: 2px;
  padding-bottom: 0px;
  padding-top: 10px;
  margin-right: 8px;
  margin-left: 12px;
  font-size: 16px;
  font-family: inherit;
  line-height: 1.5;
  min-height: ${(props) => props.rows * 24}px;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:focus {
    outline: none;
  }

  ::placeholder {
    color: ${(props) => props.emphasizeInput && EVEN_LIGHTER_GREY_COLOR};
    font-weight: ${(props) => (props.emphasizeInput ? 'bold' : 'normal')};
  }

  ${desktopMediaQuery} {
    font-size: 16px;
  }
`;

const ContainerForSendingButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
`;

const MicrophoneButton = styled.div<{
  isRecording: boolean;
  disabled: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  margin-right: 12px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${(props) => {
    if (props.disabled) return SECONDARY_TEXT_COLOR;
    return props.isRecording ? 'red' : PRIMARY_TEXT_COLOR;
  }};
  animation: ${(props) => (props.isRecording ? 'pulse 1.5s infinite' : 'none')};

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const PaperPlaneIcon = styled(FaPaperPlane)<{ disabled: boolean }>`
  transition: color 0.2s;
  color: ${(props) =>
    props.disabled ? SECONDARY_TEXT_COLOR : PRIMARY_TEXT_COLOR};
  font-size: 22px;
  cursor: pointer;
`;
